// Scroll restoration for the data router: after Back / Forward the page returns to where it was, even when its content
// arrives after the route renders (react-router's own <ScrollRestoration> restores once, before the data is there).
// A new page (another pathname) starts at the top; a change of the query alone (a toggle, a lens, a peek) leaves the scroll.
import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router';

const STORE = 'mv.scroll';
const MAX_ENTRIES = 200;
/** How long a restore keeps waiting for the content to grow tall enough. */
export const RESTORE_MS = 20_000;

type Positions = Record<string, number>;
const read = (): Positions => {
  try {
    return JSON.parse(sessionStorage.getItem(STORE) ?? '{}') as Positions;
  } catch {
    return {};
  }
};
const write = (p: Positions) => {
  try {
    const keys = Object.keys(p);
    for (const k of keys.slice(0, Math.max(0, keys.length - MAX_ENTRIES))) delete p[k];
    sessionStorage.setItem(STORE, JSON.stringify(p));
  } catch {
    // the position is a convenience
  }
};

/** Whether a restore to `target` can be done with a page this tall. */
export const reachable = (target: number, scrollHeight: number, viewport: number) => scrollHeight - viewport >= target - 1;

export function useScrollRestoration() {
  const { key, pathname } = useLocation();
  const type = useNavigationType();
  const current = useRef(key);
  const restoring = useRef(false);
  const lastPath = useRef(pathname);

  useEffect(() => {
    const prev = history.scrollRestoration;
    history.scrollRestoration = 'manual';
    let raf = 0;
    const save = () => {
      raf = 0;
      if (restoring.current) return;
      const p = read();
      p[current.current] = Math.round(window.scrollY);
      write(p);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(save);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pagehide', save);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pagehide', save);
      if (raf) cancelAnimationFrame(raf);
      history.scrollRestoration = prev;
    };
  }, []);

  useLayoutEffect(() => {
    current.current = key;
    const samePage = lastPath.current === pathname;
    lastPath.current = pathname;
    restoring.current = false;
    if (type !== 'POP') {
      if (!samePage) window.scrollTo(0, 0);
      return;
    }
    const target = read()[key];
    if (!target) return;
    restoring.current = true;
    const started = performance.now();
    let frame = 0;
    const stop = () => {
      restoring.current = false;
      cancelAnimationFrame(frame);
      ro.disconnect();
      for (const ev of ['wheel', 'touchstart', 'keydown', 'mousedown'] as const) window.removeEventListener(ev, stop);
    };
    const attempt = () => {
      window.scrollTo(0, target);
      if (reachable(target, document.documentElement.scrollHeight, window.innerHeight) && Math.abs(window.scrollY - target) <= 1) {
        // reached; stay a moment longer in case the content above still settles
        if (performance.now() - started > 600) stop();
      }
      if (performance.now() - started > RESTORE_MS) stop();
    };
    const ro = new ResizeObserver(attempt);
    ro.observe(document.body);
    const loop = () => {
      attempt();
      if (restoring.current) frame = requestAnimationFrame(loop);
    };
    loop();
    // the user taking over ends the restore
    for (const ev of ['wheel', 'touchstart', 'keydown', 'mousedown'] as const) window.addEventListener(ev, stop, { passive: true });
    return stop;
  }, [key, pathname, type]);
}
